import { useTranslation } from "src/context/Translations/context";
import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { RCCHookToast, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { convertIFileDTOArrayToFileArray } from "src/Interfaces/fileDTO";
import ModalAttachments from "src/components/ModalAttachments";
import { standardErrorMessages } from "src/utils/errors";
import { useAuth } from "src/context/Auth/context";
import { PermissionEnum } from "src/context/Auth/interface";
import { checkSituationOccurrence, IOccurrence, IRefModalAttachments } from "../interface";

const modalAttachments : React.ForwardRefRenderFunction<IRefModalAttachments> = (_, ref) => {
	
	const { administrator, superUser, checkIfHasSpecificPermission } = useAuth();
	const { translate } = useTranslation();
	const refModalAttachment = useRef<RCCRefModalProps>(null);
	const { setLoading } = useLoaderBackdrop();
	const { addToast } = RCCHookToast();

	const [occurenceId, setOccurenceId] = useState<number>(0);
	const [subtitle, setSubtitle] = useState<string | undefined>(undefined);
	const [attachments, setAttachments] = useState<File[]>([]);
	const [disabled, setDisabled] = useState(false);

	const onCallbackFunction = useRef<Function | undefined>(undefined);

	useImperativeHandle(ref, () => ({
		actionButtonAttachments
	}));

	const actionButtonAttachments = (occurence: IOccurrence, callbackChange?: Function) => {
		setDisabled(disabledAttachments(occurence));
		onCallbackFunction.current  = callbackChange;
		setSubtitle(`${translate("Ocorrência")}: ${occurence.fullTitle}`);
		setOccurenceId(occurence.ocb001Id);
		queryAttachments(occurence.ocb001Id);
	};

	const disabledAttachments = (occurence: IOccurrence) => {
		const statusFinishedCanceledDisapproved = occurence.ocb001Status === checkSituationOccurrence.CANCELED || occurence.ocb001Status === checkSituationOccurrence.FINISHED || occurence.ocb001Status === checkSituationOccurrence.DISAPPROVED;
		if(statusFinishedCanceledDisapproved)
			return true;

		const isAdminOrSuper = administrator || superUser;
		if(isAdminOrSuper)
			return false;

		const canCreateUpdate = checkIfHasSpecificPermission("/occurrences/panel", PermissionEnum.CREATE_UPDATE);
		if(!canCreateUpdate)
			return true;

		return false;
	};

	const queryAttachments= async (id: number) => {
		try{
			setLoading(true);

			const {data} = await RCCServicesApi.getRequest({
				method: "Occurrences/SelectAttachments",
				urlParameters: {
					id
				}
			});
			let dataFormatted: File[] = [];

			if(data && data.length > 0)
				dataFormatted = convertIFileDTOArrayToFileArray(data);

			setAttachments(dataFormatted);
			setLoading(false);

			refModalAttachment.current?.openModal();
		}catch(err){
			setLoading(false);
		}
	};
  
	const onAddAttachment = useCallback(async (data: any) => {
		try{
			setLoading(true);
			const formData = new FormData();

			formData.append("occurenceId", occurenceId.toString());
      
			data.attachments.forEach((attach: any, index: number) => {
				formData.append(`Attachments[${index}]`, attach);
			});

			await RCCServicesApi.postRequest({
				method: "Occurrences/InsertAttachments",
				contentType: "multipart/form-data",
				body: formData
			});

			addToast({
				title: translate("Sucesso"),
				description: translate("Anexo inserido com sucesso"),
				color: "green"
			});

			onCallbackFunction.current?.();

			refModalAttachment.current?.closeModal();
				
			setLoading(false);

		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao adicionar anexos"), addToast);
		}
	}, [occurenceId]);

	return (
		<>
      	<ModalAttachments 
				ref={refModalAttachment}
				subtitle={subtitle}
				filesInit={attachments}
				name="attachments"
				onSubmit={onAddAttachment}
				disabled={disabled}
			  />
		</>
	);
};

export default forwardRef(modalAttachments);
